// Haptic feedback — two parallel strategies:
//   Android Chrome:  navigator.vibrate() (Vibration API)
//   iOS Safari 17.4+: checkbox switch trick — clicking an <input switch> via its label
//                     triggers the Taptic Engine. Requires a user gesture in the call stack.
//
// The global click listener that fires tap haptic on every button is in
// lib/mobileInit.ts (imported from main.tsx). lastGlobalHapticMs is shared so
// h.tap() can skip when the global already fired for the same click.

import { lastGlobalHapticMs } from '../mobileInit';

const canVibrate = typeof navigator !== 'undefined' && 'vibrate' in navigator;

function vibrate(pattern: number | number[]): void {
  if (!canVibrate) return;
  try { navigator.vibrate(pattern); } catch { /* silently ignore */ }
}

function iosHaptic(count = 1, delayMs = 80): void {
  function tick() {
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    input.style.cssText = 'position:fixed;top:-200px;left:-200px;opacity:0;pointer-events:none;';

    const label = document.createElement('label');
    const uid = `_h${Date.now()}${Math.random()}`;
    input.id = uid;
    label.htmlFor = uid;

    document.body.appendChild(input);
    document.body.appendChild(label);
    label.click();

    requestAnimationFrame(() => {
      input.remove();
      label.remove();
    });
  }

  tick();
  for (let i = 1; i < count; i++) {
    setTimeout(tick, i * delayMs);
  }
}

const isIOS = (() => {
  if (typeof document === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
})();

export interface HapticFunctions {
  tap: () => void;
  correct: () => void;
  incorrect: () => void;
  drag: () => void;
  complete: () => void;
}

export function useHaptics(): HapticFunctions {
  if (isIOS) {
    return {
      // Skip if the global listener already fired for this touch (within 150 ms)
      tap:       () => { if (Date.now() - lastGlobalHapticMs < 150) return; iosHaptic(1); },
      correct:   () => iosHaptic(2, 80),
      incorrect: () => iosHaptic(1),
      drag:      () => iosHaptic(1),
      complete:  () => iosHaptic(3, 120),
    };
  }

  return {
    tap:       () => { if (Date.now() - lastGlobalHapticMs < 150) return; vibrate(30); },
    correct:   () => vibrate([50, 30, 50]),
    incorrect: () => vibrate(200),
    drag:      () => vibrate(15),
    complete:  () => vibrate([100, 50, 100, 50, 200]),
  };
}
